import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { eur } from "@/lib/api";
import { Check, X, Bell, Utensils, StickyNote, Users, Printer } from "lucide-react";

const STATUS = {
  NOUVELLE: { label: "Nouvelle", chip: "bg-[#C69C4A]/15 text-[#E7C97A]" },
  EN_PREPARATION: { label: "En préparation", chip: "bg-blue-400/15 text-blue-200" },
  PRETE: { label: "Prête", chip: "bg-emerald-400/15 text-emerald-200" },
  SERVIE: { label: "Servie", chip: "bg-white/10 text-stone-300" },
  REFUSEE: { label: "Refusée", chip: "bg-red-500/15 text-red-300" },
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "");
const methodLabel = (m) => (m === "cash" ? "Espèces" : m === "card" ? "CB" : m === "ticket" ? "Ticket resto" : m || "");

export default function OrderDetailDrawer({ order, open, onClose, onStatus, onTableView, onTicket, payments = [] }) {
  if (!order) return null;
  const meta = STATUS[order.status] || STATUS.NOUVELLE;
  const num = order.order_number.replace("#", "");
  const pay = payments.find(
    (p) => (p.covered_guests || []).includes(order.guest_name || "Invité") && p.table_session_id === order.table_session_id && p.payment_status === "paid"
  );
  const paid = order.paid || !!pay;

  const act = (id, status, label, Icon, cls) => (
    <Button data-testid={`detail-btn-${id}`} onClick={() => onStatus(order.id, status)} className={`flex-1 rounded-xl h-11 font-bold ${cls}`}>
      <Icon className="w-4 h-4 mr-1.5" /> {label}
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" data-testid="order-detail-drawer" className="w-full sm:max-w-md bg-[#0C1713] border-l border-[#1B3028] text-stone-100 p-0 flex flex-col">
        <SheetHeader className="p-5 border-b border-[#1B3028] text-left space-y-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="font-serif-display text-3xl text-stone-100 leading-none">Table {order.table_number}</SheetTitle>
              <SheetDescription data-testid="order-detail-number" className="text-stone-400 mt-1.5">
                {order.order_code || order.order_number} · {fmtDate(order.created_at)} {fmtTime(order.created_at)}
                {order.guest_name ? ` · 👤 ${order.guest_name}` : ""}
              </SheetDescription>
            </div>
            <span data-testid="order-detail-status" className={`shrink-0 text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full ${meta.chip}`}>{meta.label}</span>
          </div>
          <button data-testid="order-detail-table-view" onClick={() => onTableView(order)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C69C4A] hover:text-[#E7C97A]">
            <Users className="w-3.5 h-3.5" /> Voir toute la table
          </button>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {order.items.map((it, k) => {
            const hasSupp = (it.supplements || []).length > 0;
            return (
              <div key={k} className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-4">
                <div className="flex justify-between items-start gap-3">
                  <p className="text-white font-bold text-lg leading-tight">{it.quantity}× {it.name}</p>
                  <span className="text-[#C69C4A] font-bold shrink-0">{eur(it.line_total)}</span>
                </div>
                <div className="mt-2 space-y-1 text-sm text-stone-300">
                  {(it.formula || []).map((f, i) => (
                    <p key={`f${i}`}><span className="text-stone-500">{f.course} :</span> <b className="text-white">{f.choice}</b>{f.price ? ` (+${eur(f.price)})` : ""}</p>
                  ))}
                  {it.cooking && <p><span className="text-stone-500">Cuisson :</span> <b className="text-white">{it.cooking}</b></p>}
                  {it.side && <p><span className="text-stone-500">Accompagnement :</span> <b className="text-white">{it.side}</b></p>}
                  {(it.options || []).map((o, i) => (
                    <p key={`o${i}`}><span className="text-stone-500">{o.group ? `${o.group} :` : "Option :"}</span> <b className="text-white">{o.value}</b>{o.price ? ` (+${eur(o.price)})` : ""}</p>
                  ))}
                  {hasSupp && (
                    <div>
                      <span className="text-stone-500">Suppléments :</span>
                      {(it.supplements || []).map((s, i) => (
                        <p key={i} className="pl-3">• {s.name}{s.price ? ` — ${eur(s.price)}` : ""}</p>
                      ))}
                    </div>
                  )}
                </div>
                {it.instructions && (
                  <div data-testid="order-detail-instruction" className="mt-3 rounded-xl border border-[#E7C97A]/50 bg-[#E7C97A]/10 px-3 py-2.5">
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#E7C97A] flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Instruction client</p>
                    <p className="text-[#F5E4B8] font-semibold mt-1 leading-snug">{it.instructions}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-[#1B3028] p-5 space-y-3 shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-sm uppercase tracking-wide text-stone-500">Paiement</span>
            <span data-testid="order-detail-payment" className={`text-sm font-bold ${paid ? "text-emerald-300" : "text-stone-300"}`}>
              {paid ? `Payé${pay?.method ? ` · ${methodLabel(pay.method)}` : ""}` : "Non réglé"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm uppercase tracking-wide text-stone-500">Total</span>
            <span data-testid="order-detail-total" className="font-serif-display text-2xl font-bold text-[#C69C4A]">{eur(order.total)}</span>
          </div>
          {order.status !== "NOUVELLE" && (
            <button data-testid="order-detail-ticket" onClick={() => onTicket(order)} className="w-full text-xs text-stone-400 hover:text-[#C69C4A] font-semibold inline-flex items-center justify-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Ticket cuisine
            </button>
          )}
          <div className="flex gap-2">
            {order.status === "NOUVELLE" && (
              <>
                {act("refuse", "REFUSEE", "Refuser", X, "bg-transparent border border-red-500/40 text-red-400 hover:bg-red-500/10")}
                {act("accept", "EN_PREPARATION", "Accepter", Check, "bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713]")}
              </>
            )}
            {order.status === "EN_PREPARATION" && act("ready", "PRETE", "Marquer prête", Bell, "bg-emerald-600 hover:bg-emerald-700 text-white")}
            {order.status === "PRETE" && act("served", "SERVIE", "Servie", Utensils, "bg-[#1A3D32] hover:bg-[#123026] text-stone-100 border border-[#2A5344]")}
            {(order.status === "SERVIE" || order.status === "REFUSEE") && (
              <p className="flex-1 text-center text-sm text-stone-500 py-2.5">Commande clôturée · consultable dans l'historique</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
